<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WahaIntegrationsAPI from 'dashboard/api/wahaIntegrations';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  channelIdentifier: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();

const initialLoading = ref(true);
const refreshing = ref(false);
const error = ref(null);
const sessionStatus = ref('');
const qrCode = ref(null);
const loadingQR = ref(false);
const actionLoading = ref(false);
const showQRModal = ref(false);
let pollingInterval = null;

const statusColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'text-green-600 dark:text-green-400';
    case 'SCAN_QR_CODE':
      return 'text-blue-600 dark:text-blue-400';
    case 'STARTING':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'STOPPED':
      return 'text-slate-600 dark:text-slate-400';
    case 'FAILED':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-slate-600 dark:text-slate-400';
  }
});

const statusBgColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'bg-green-100 dark:bg-green-900/30';
    case 'SCAN_QR_CODE':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'STARTING':
      return 'bg-yellow-100 dark:bg-yellow-900/30';
    case 'STOPPED':
      return 'bg-slate-100 dark:bg-slate-700';
    case 'FAILED':
      return 'bg-red-100 dark:bg-red-900/30';
    default:
      return 'bg-slate-100 dark:bg-slate-700';
  }
});

const statusDotColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'bg-green-600 dark:bg-green-400 animate-pulse';
    case 'SCAN_QR_CODE':
      return 'bg-blue-600 dark:bg-blue-400 animate-pulse';
    case 'STARTING':
      return 'bg-yellow-600 dark:bg-yellow-400 animate-pulse';
    case 'STOPPED':
      return 'bg-slate-600 dark:bg-slate-400';
    case 'FAILED':
      return 'bg-red-600 dark:bg-red-400 animate-pulse';
    default:
      return 'bg-slate-600 dark:bg-slate-400';
  }
});

const sessionStatusText = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_CONNECTED');
    case 'SCAN_QR_CODE':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_SCANNING');
    case 'STARTING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STARTING');
    case 'STOPPED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STOPPED');
    case 'FAILED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_FAILED');
    default:
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_UNKNOWN');
  }
});

const statusDescription = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_CONNECTED_DESC');
    case 'SCAN_QR_CODE':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_SCANNING_DESC');
    case 'STARTING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STARTING_DESC');
    case 'STOPPED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STOPPED_DESC');
    case 'FAILED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_FAILED_DESC');
    default:
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_UNKNOWN_DESC');
  }
});

const startPolling = () => {
  stopPolling();
  pollingInterval = setInterval(() => {
    fetchStatus(true);
  }, 3000);
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

const fetchStatus = async (isRefresh = false) => {
  const timeoutId = setTimeout(() => {
    if (initialLoading.value || refreshing.value) {
      error.value = t('INBOX_MGMT.WAHA_SETTINGS.TIMEOUT_ERROR');
      initialLoading.value = false;
      refreshing.value = false;
    }
  }, 10000);

  try {
    if (isRefresh) {
      refreshing.value = true;
    } else {
      initialLoading.value = true;
    }
    error.value = null;

    const response = await WahaIntegrationsAPI.getSessionStatus(
      props.channelIdentifier
    );
    clearTimeout(timeoutId);

    if (response && response.data) {
      const newStatus = response.data.status || 'UNKNOWN';
      const oldStatus = sessionStatus.value;
      sessionStatus.value = newStatus;

      if (newStatus === 'SCAN_QR_CODE') {
        await fetchQRCode();
        startPolling();
      } else if (newStatus === 'STARTING') {
        qrCode.value = null;
        startPolling();
      } else if (newStatus === 'WORKING' && oldStatus === 'SCAN_QR_CODE') {
        qrCode.value = null;
        stopPolling();
      } else if (['WORKING', 'STOPPED', 'FAILED'].includes(newStatus)) {
        qrCode.value = null;
        stopPolling();
      }
    } else {
      throw new Error('Respuesta inválida del servidor');
    }
  } catch (err) {
    clearTimeout(timeoutId);
    console.error('Error fetching status:', err);
    error.value =
      err.response?.data?.error ||
      err.message ||
      t('INBOX_MGMT.WAHA_SETTINGS.CONNECTION_ERROR');
    sessionStatus.value = 'UNKNOWN';
    stopPolling();
  } finally {
    initialLoading.value = false;
    refreshing.value = false;
  }
};

const fetchQRCode = async () => {
  try {
    loadingQR.value = true;
    const response = await WahaIntegrationsAPI.getQRCode(
      props.channelIdentifier
    );
    qrCode.value = response.data;
  } catch (err) {
    console.error('Error fetching QR code:', err);
  } finally {
    loadingQR.value = false;
  }
};

const refreshStatus = async () => {
  await fetchStatus(true);
};

const startSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.startSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al iniciar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const stopSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.stopSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al detener la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const restartSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.restartSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al reiniciar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const logoutSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.logoutSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al cerrar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

onMounted(() => {
  fetchStatus();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div
    class="border border-slate-200 dark:border-slate-600 rounded-lg p-6 bg-white dark:bg-slate-800"
  >
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {{ $t('INBOX_MGMT.WAHA_SETTINGS.TITLE') }}
      </h3>
      <button
        :disabled="refreshing"
        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-600 border border-slate-300 dark:border-slate-500 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-500 disabled:opacity-50 transition-opacity"
        @click="refreshStatus"
      >
        <span v-if="refreshing">{{
          $t('INBOX_MGMT.WAHA_SETTINGS.UPDATING')
        }}</span>
        <span v-else>{{ $t('INBOX_MGMT.WAHA_SETTINGS.UPDATE_BUTTON') }}</span>
      </button>
    </div>

    <div
      v-if="error"
      class="p-4 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
    >
      <p class="text-red-800 dark:text-red-200">{{ error }}</p>
    </div>

    <div v-if="initialLoading" class="flex items-center justify-center py-8">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-3"
        />
        <span class="text-slate-600 dark:text-slate-400">{{
          $t('INBOX_MGMT.WAHA_SETTINGS.LOADING_SESSION')
        }}</span>
      </div>
    </div>

    <div v-else class="space-y-6">
      <!-- Status Card con transición suave -->
      <transition name="fade" mode="out-in">
        <div
          :key="sessionStatus"
          class="p-5 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
        >
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-full flex items-center justify-center"
              :class="statusBgColor"
            >
              <div class="w-3 h-3 rounded-full" :class="statusDotColor" />
            </div>
            <div class="flex-1">
              <p
                class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide"
              >
                {{ $t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STATUS') }}
              </p>
              <p class="text-xl font-bold mt-1" :class="statusColor">
                {{ sessionStatusText }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ statusDescription }}
              </p>
            </div>
          </div>
        </div>
      </transition>

      <!-- QR Code Section con transición -->
      <transition name="slide-fade">
        <div v-if="sessionStatus === 'SCAN_QR_CODE'" class="space-y-4">
          <div
            class="p-5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
          >
            <div class="mb-4">
              <div class="flex items-center gap-2 mb-2">
                <svg
                  class="w-5 h-5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <h4
                  class="text-base font-semibold text-blue-900 dark:text-blue-100"
                >
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_TITLE') }}
                </h4>
              </div>
              <ol
                class="text-sm text-blue-800 dark:text-blue-200 space-y-2 ml-4 list-decimal"
              >
                <li v-html="$t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_1')" />
                <li v-html="$t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_2')" />
                <li v-html="$t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_3')" />
                <li>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_4') }}</li>
              </ol>
            </div>

            <div class="flex justify-center">
              <transition name="fade" mode="out-in">
                <div v-if="loadingQR" key="loading" class="text-center py-12">
                  <div
                    class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"
                  />
                  <span class="text-slate-600 dark:text-slate-400">{{
                    $t('INBOX_MGMT.WAHA_SETTINGS.QR_LOADING')
                  }}</span>
                </div>
                <div v-else-if="qrCode" key="qr" class="relative">
                  <img
                    :src="`data:image/png;base64,${qrCode}`"
                    alt="QR Code"
                    class="w-full max-w-md h-auto rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                    @click="showQRModal = true"
                  />
                  <button
                    class="absolute bottom-3 right-3 px-4 py-2 text-sm font-medium text-white bg-slate-900/80 hover:bg-slate-900 rounded-lg transition-colors flex items-center gap-2"
                    @click="showQRModal = true"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                    {{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_FULL_SIZE') }}
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </transition>

      <!-- Action Buttons -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button
          :disabled="actionLoading"
          class="px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.START_SESSION_TITLE')"
          @click="startSession"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('INBOX_MGMT.WAHA_SETTINGS.START_SESSION') }}
        </button>
        <button
          :disabled="actionLoading"
          class="px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.STOP_SESSION_TITLE')"
          @click="stopSession"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('INBOX_MGMT.WAHA_SETTINGS.STOP_SESSION') }}
        </button>
        <button
          :disabled="actionLoading"
          class="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.RESTART_SESSION_TITLE')"
          @click="restartSession"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('INBOX_MGMT.WAHA_SETTINGS.RESTART_SESSION') }}
        </button>
        <button
          :disabled="actionLoading"
          class="px-4 py-2.5 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.LOGOUT_SESSION_TITLE')"
          @click="logoutSession"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('INBOX_MGMT.WAHA_SETTINGS.LOGOUT_SESSION') }}
        </button>
      </div>

      <!-- Action feedback -->
      <transition name="fade">
        <div
          v-if="actionLoading"
          class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"
            />
            <span class="text-sm text-blue-800 dark:text-blue-200">{{
              $t('INBOX_MGMT.WAHA_SETTINGS.PROCESSING_ACTION')
            }}</span>
          </div>
        </div>
      </transition>
    </div>

    <div
      v-if="showQRModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      @click="showQRModal = false"
      @keydown.esc="showQRModal = false"
    >
      <div class="relative max-w-2xl w-full p-4" @click.stop>
        <img
          :src="`data:image/png;base64,${qrCode}`"
          alt="QR Code"
          class="w-full h-auto rounded-lg shadow-2xl"
        />
        <button
          class="absolute top-6 right-6 px-4 py-2 text-sm font-medium text-white bg-slate-900/70 hover:bg-slate-900/90 rounded-lg"
          @click="showQRModal = false"
        >
          {{ $t('INBOX_MGMT.WAHA_SETTINGS.CLOSE_MODAL') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
